import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import DashboardLayout from '../Layout/DashboardLayout';
import LoadingSpinner from '../Common/LoadingSpinner';
import './Admin.css';

const AdminAnalytics = () => {
    const { dashboardStats, loading, getDashboardStats, getUsers, getUserDetails } = useAdmin();
    const [viewType, setViewType] = useState('system'); // 'system' or 'user'
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userStats, setUserStats] = useState(null);
    const [isSearching, setIsSearching] = useState(false);

    // Date Range State
    const [dateRange, setDateRange] = useState({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        refreshData();
    }, [dateRange, viewType, selectedUser?._id]);

    const refreshData = async () => {
        if (viewType === 'system') {
            await getDashboardStats(dateRange);
        } else if (selectedUser) {
            try {
                const response = await getUserDetails(selectedUser._id, dateRange);
                if (response.success) {
                    setUserStats(response.user.stats);
                }
            } catch (err) {
                console.error('Failed to fetch user details:', err);
            }
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchTerm.trim()) return;

        setIsSearching(true);
        try {
            const response = await getUsers({ search: searchTerm, limit: 5 });
            setSearchResults(response.users || []);
        } catch (err) {
            console.error('Search failed:', err);
        } finally {
            setIsSearching(false);
        }
    };

    const selectUser = (user) => {
        setSelectedUser(user);
        setViewType('user');
        setSearchResults([]);
        setSearchTerm('');
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setDateRange(prev => ({ ...prev, [name]: value }));
    };

    if (loading && !dashboardStats && viewType === 'system') {
        return (
            <DashboardLayout isAdmin={true}>
                <div className="admin-page">
                    <LoadingSpinner />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout isAdmin={true}>
            <div className="admin-page">
                <header className="admin-header flex-column align-items-start gap-3">
                    <div className="d-flex justify-content-between w-100 align-items-center">
                        <h1>{viewType === 'system' ? 'System Analytics' : `Analytics: ${selectedUser?.name}`}</h1>
                        <button
                            className={`btn ${viewType === 'system' ? 'btn--primary' : 'btn--outline'}`}
                            onClick={() => setViewType('system')}
                        >
                            System View
                        </button>
                    </div>

                    <div className="admin-controls d-flex flex-wrap gap-4 w-100 p-3 bg-secondary rounded shadow-sm">
                        <div className="search-container">
                            <label className="form-label mb-1 fw-bold small text-uppercase">User Search</label>
                            <form onSubmit={handleSearch} className="d-flex gap-2">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search user..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <button type="submit" className="btn btn--primary">Search</button>
                            </form>
                            {searchResults.length > 0 && (
                                <div className="search-results-dropdown shadow-lg">
                                    {searchResults.map(u => (
                                        <div key={u._id} className="search-result-item" onClick={() => selectUser(u)}>
                                            {u.name} ({u.email})
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="date-range-controls d-flex gap-3 align-items-end">
                            <div className="date-input">
                                <label className="form-label mb-1 fw-bold small text-uppercase">From</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="startDate"
                                    value={dateRange.startDate}
                                    onChange={handleDateChange}
                                />
                            </div>
                            <div className="date-input">
                                <label className="form-label mb-1 fw-bold small text-uppercase">To</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="endDate"
                                    value={dateRange.endDate}
                                    onChange={handleDateChange}
                                />
                            </div>
                        </div>
                    </div>
                </header>

                {viewType === 'system' ? (
                    <>
                        <div className="admin-stats-grid">
                            <div className="stat-card">
                                <span className="stat-label">Total Messages</span>
                                <span className="stat-value">{dashboardStats?.platform?.totalMessages?.toLocaleString() || 0}</span>
                                <div className="stat-change up">
                                    <i className="fas fa-arrow-up"></i>
                                    <span>12.5% vs prev period</span>
                                </div>
                            </div>
                            <div className="stat-card">
                                <span className="stat-label">System Revenue</span>
                                <span className="stat-value">₹{dashboardStats?.wallet?.totalBalance?.toLocaleString() || 0}</span>
                                <div className="stat-change up">
                                    <i className="fas fa-arrow-up"></i>
                                    <span>8.2% vs prev period</span>
                                </div>
                            </div>
                            <div className="stat-card">
                                <span className="stat-label">Active Users</span>
                                <span className="stat-value">{dashboardStats?.users?.activeUsers || 0}</span>
                                <div className="stat-change up">
                                    <i className="fas fa-arrow-up"></i>
                                    <span>5.1% vs prev period</span>
                                </div>
                            </div>
                            <div className="stat-card">
                                <span className="stat-label">System Health</span>
                                <span className="stat-value">99.8%</span>
                                <div className="stat-change up">
                                    <span>Normal</span>
                                </div>
                            </div>
                        </div>

                        <div className="admin-section">
                            <h2>Message Distribution</h2>
                            <div className="admin-table-container">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Category</th>
                                            <th>Volume</th>
                                            <th>Success Rate</th>
                                            <th>Revenue</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dashboardStats?.messageDistribution && Object.keys(dashboardStats.messageDistribution).length > 0 ? (
                                            Object.entries(dashboardStats.messageDistribution).map(([category, data]) => (
                                                <tr key={category}>
                                                    <td style={{ textTransform: 'capitalize' }}>{category}</td>
                                                    <td>{data.count.toLocaleString()}</td>
                                                    <td>
                                                        <span className={data.successRate >= 95 ? 'text-success' : data.successRate >= 80 ? 'text-warning' : 'text-danger'}>
                                                            {data.successRate}%
                                                        </span>
                                                    </td>
                                                    <td>₹{data.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" style={{ textAlign: 'center' }}>No message distribution data available</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="admin-stats-grid">
                            <div className="stat-card">
                                <span className="stat-label">Campaigns Triggered</span>
                                <span className="stat-value">{userStats?.billing?.totalCampaigns || 0}</span>
                            </div>
                            <div className="stat-card">
                                <span className="stat-label">Templates Used</span>
                                <span className="stat-value">{userStats?.templates?.total || 0}</span>
                                <div className="stat-change text-muted">
                                    <span>Approved: {userStats?.templates?.approved || 0}</span>
                                </div>
                            </div>
                            <div className="stat-card">
                                <span className="stat-label">Active Flows</span>
                                <span className="stat-value">{userStats?.flows?.active || 0}</span>
                                <div className="stat-change text-muted">
                                    <span>Total: {userStats?.flows?.total || 0}</span>
                                </div>
                            </div>
                            <div className="stat-card">
                                <span className="stat-label">Total Spend</span>
                                <span className="stat-value">₹{userStats?.billing?.totalSpend?.toFixed(2) || '0.00'}</span>
                            </div>
                        </div>

                        <div className="admin-section">
                            <h2>Message Statistics</h2>
                            <div className="admin-table-container">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Total</th>
                                            <th>Sent</th>
                                            <th>Delivered</th>
                                            <th>Read</th>
                                            <th>Failed</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {userStats?.messages?.length > 0 ? userStats.messages.map(m => (
                                            <tr key={m._id}>
                                                <td>{m._id.charAt(0).toUpperCase() + m._id.slice(1)}</td>
                                                <td>{m.total}</td>
                                                <td>{m.sent}</td>
                                                <td>{m.delivered}</td>
                                                <td>{m.read}</td>
                                                <td>{m.failed}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="6" className="text-center">No messaging data found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </DashboardLayout>
    );
};

export default AdminAnalytics;
